/**
 * Clothify theme — core interactions: drawers/modals, header nav,
 * announcement bar, hero slideshow, AJAX cart, predictive search,
 * collection filters, accordions, newsletter.
 */
(function () {
  'use strict';

  var theme = window.theme || { settings: {}, strings: {}, routes: {} };

  /* ============================================================
     Utilities
     ============================================================ */
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function formatMoney(cents, format) {
    if (cents == null) return '';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var decimals = parts[1];
    var placeholders = {
      amount: dollars + '.' + decimals,
      amount_no_decimals: dollars,
      amount_with_comma_separator: dollars + ',' + decimals,
      amount_no_decimals_with_comma_separator: dollars
    };
    var fmt = format || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, key) {
      return placeholders[key] != null ? placeholders[key] : placeholders.amount;
    });
  }
  window.ClothifyFormatMoney = formatMoney;

  /* ============================================================
     Store-wide polish: reveal-on-scroll + back-to-top affordance
     ============================================================ */
  (function initStorePolish() {
    var revealEls = document.querySelectorAll('.section, .product-card, .collection-tile, .testimonial, .image-with-text__media, .image-with-text__content');
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      revealEls.forEach(function (el) {
        el.classList.add('reveal-on-scroll');
        observer.observe(el);
      });
    } else {
      revealEls.forEach(function (el) { el.classList.add('is-visible'); });
    }

    var topBtn = document.querySelector('[data-back-to-top]');
    if (!topBtn) return;
    function updateTopButton() {
      topBtn.classList.toggle('is-visible', window.scrollY > 700);
    }
    topBtn.addEventListener('click', function () { window.scrollTo({ top: 0, behavior: 'smooth' }); });
    updateTopButton();
    window.addEventListener('scroll', debounce(updateTopButton, 50), { passive: true });
  })();

  /* ============================================================
     Transparent overlay header (homepage hero) — solidifies once
     the page scrolls past the hero, editorial-look header. Pulls
     the hero section up underneath it via a measured CSS variable
     instead of position:absolute, so it never overlaps the
     in-flow announcement bar rendered above it.
     ============================================================ */
  (function initTransparentHeader() {
    var wrapper = document.querySelector('[data-header-wrapper].header-wrapper--transparent');
    if (!wrapper) return;
    var hero = document.querySelector('.hero');
    var threshold = hero ? Math.max(hero.offsetHeight - 120, 80) : 120;

    function measure() {
      document.documentElement.style.setProperty('--transparent-header-h', wrapper.offsetHeight + 'px');
    }

    function update() {
      wrapper.classList.toggle('is-scrolled', window.scrollY > threshold);
    }
    measure();
    update();
    window.addEventListener('scroll', debounce(update, 10), { passive: true });
    window.addEventListener('resize', debounce(function () { measure(); update(); }, 150));
  })();

  /* ============================================================
     Scrolled header state — drives shrink-on-scroll and the glass
     effect. Deliberately separate from the transparent-hero logic
     above: that one fires only past the homepage hero, whereas this
     must apply on every page as soon as the page moves at all. Uses
     its own `is-stuck` class so the two never fight.
     ============================================================ */
  (function initStickyHeaderState() {
    var wrapper = document.querySelector('[data-header-wrapper]');
    if (!wrapper) return;

    function update() {
      wrapper.classList.toggle('is-stuck', window.scrollY > 8);
    }
    update();
    window.addEventListener('scroll', debounce(update, 10), { passive: true });
  })();

  var focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    if (event.key !== 'Tab') return;
    var focusable = container.querySelectorAll(focusableSelector);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ============================================================
     Drawers & modals (cart, search, mobile nav, filters, quick add, size guide)
     ============================================================ */
  var lastFocusedEl = null;

  function lockBodyScroll() { document.documentElement.style.overflow = 'hidden'; }
  function unlockBodyScroll() {
    var anyOpen = document.querySelector('.drawer.is-open, .modal.is-open');
    if (!anyOpen) document.documentElement.style.overflow = '';
  }

  function openOverlay(el) {
    if (!el) return;
    lastFocusedEl = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lockBodyScroll();
    var panel = el.querySelector('.drawer__panel, .modal__panel');
    if (panel) {
      var focusTarget = panel.querySelector('[data-search-input]') || panel.querySelector(focusableSelector);
      if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 50);
    }
    el.addEventListener('keydown', overlayKeydown);
  }

  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();
    el.removeEventListener('keydown', overlayKeydown);
    if (lastFocusedEl && document.body.contains(lastFocusedEl)) lastFocusedEl.focus();
  }

  function overlayKeydown(e) {
    if (e.key === 'Escape') {
      closeOverlay(e.currentTarget);
      return;
    }
    var panel = e.currentTarget.querySelector('.drawer__panel, .modal__panel');
    if (panel) trapFocus(panel, e);
  }

  document.addEventListener('click', function (e) {
    var opener;

    if ((opener = e.target.closest('[data-cart-drawer-toggle]'))) {
      e.preventDefault();
      openOverlay(document.getElementById('CartDrawer'));
    } else if ((opener = e.target.closest('[data-cart-drawer-close]'))) {
      closeOverlay(document.getElementById('CartDrawer'));
    } else if ((opener = e.target.closest('[data-search-drawer-toggle]'))) {
      e.preventDefault();
      openOverlay(document.getElementById('SearchDrawer'));
    } else if ((opener = e.target.closest('[data-search-drawer-close]'))) {
      closeOverlay(document.getElementById('SearchDrawer'));
    } else if ((opener = e.target.closest('[data-mobile-nav-open]'))) {
      e.preventDefault();
      openOverlay(document.getElementById('MobileNav'));
      opener.setAttribute('aria-expanded', 'true');
    } else if ((opener = e.target.closest('[data-mobile-nav-close]'))) {
      closeOverlay(document.getElementById('MobileNav'));
      var toggle = document.querySelector('[data-mobile-nav-open]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    } else if ((opener = e.target.closest('[data-filters-drawer-open]'))) {
      e.preventDefault();
      openOverlay(document.getElementById('FiltersDrawer'));
    } else if ((opener = e.target.closest('[data-filters-drawer-close]'))) {
      closeOverlay(document.getElementById('FiltersDrawer'));
    } else if ((opener = e.target.closest('[data-size-guide-open]'))) {
      e.preventDefault();
      openOverlay(document.getElementById('SizeGuideModal'));
    } else if ((opener = e.target.closest('[data-size-guide-close]'))) {
      closeOverlay(document.getElementById('SizeGuideModal'));
    } else if ((opener = e.target.closest('[data-quick-add-close]'))) {
      closeOverlay(document.getElementById('QuickAddModal'));
    }
  });

  /* ============================================================
     Mobile nav submenu accordion
     ============================================================ */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-mobile-submenu-toggle]');
    if (!toggle) return;
    var expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    var submenu = toggle.nextElementSibling;
    if (submenu) submenu.classList.toggle('is-open', !expanded);
  });

  /* ============================================================
     Desktop mega menu (keyboard / touch accessible; hover handled by CSS)
     ============================================================ */
  function closeAllMegaMenus() {
    document.querySelectorAll('[data-mega-menu-toggle][aria-expanded="true"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
    document.querySelectorAll('.mega-menu.is-open').forEach(function (menu) {
      menu.classList.remove('is-open');
    });
  }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-mega-menu-toggle]');
    if (toggle) {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      var menu = document.getElementById(toggle.getAttribute('aria-controls'));
      closeAllMegaMenus();
      if (!expanded) {
        toggle.setAttribute('aria-expanded', 'true');
        if (menu) menu.classList.add('is-open');
      }
      return;
    }
    if (!e.target.closest('.header__nav-item')) closeAllMegaMenus();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllMegaMenus();
  });

  /* ============================================================
     Announcement bar rotation
     ============================================================ */
  (function initAnnouncementBar() {
    var bar = document.querySelector('[data-announcement-bar]');
    if (!bar) return;
    var items = bar.querySelectorAll('[data-announcement-item]');
    var track = bar.querySelector('[data-announcement-track]');
    if (items.length < 2 || !track) return;
    var speed = parseFloat(bar.dataset.rotateSpeed || '5') * 1000;
    var index = 0;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;
    setInterval(function () {
      index = (index + 1) % items.length;
      track.style.transform = 'translateX(-' + (index * 100) + '%)';
    }, speed);
  })();

  /* ============================================================
     Product carousel (horizontal scroll-snap, arrow controls)
     ============================================================ */
  (function initProductCarousels() {
    document.querySelectorAll('[data-product-carousel]').forEach(function (carousel) {
      var track = carousel.querySelector('[data-carousel-track]');
      var header = carousel.closest('.section') && carousel.closest('.section').querySelector('.carousel-header');
      var prevBtn = header && header.querySelector('[data-carousel-prev]');
      var nextBtn = header && header.querySelector('[data-carousel-next]');
      if (!track || (!prevBtn && !nextBtn)) return;

      function step() {
        var item = track.querySelector('.product-carousel__item');
        return item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      }

      function updateEdges() {
        var atStart = track.scrollLeft <= 2;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        if (prevBtn) prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        if (nextBtn) nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (nextBtn) nextBtn.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', debounce(updateEdges, 100));
      updateEdges();
    });
  })();

  /* ============================================================
     Hero slideshow
     ============================================================ */
  (function initHeroSlideshows() {
    document.querySelectorAll('[data-hero-slideshow]').forEach(function (hero) {
      var slides = hero.querySelectorAll('[data-hero-slide]');
      var dots = hero.querySelectorAll('[data-hero-dot]');
      if (slides.length < 2) return;
      var index = 0;
      var timer;
      var autoplay = hero.dataset.autoplay === 'true';
      var speed = parseInt(hero.dataset.autoplaySpeed, 10) || 6000;
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function show(i) {
        index = (i + slides.length) % slides.length;
        slides.forEach(function (slide, slideIndex) {
          var isActive = slideIndex === index;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
        });
        dots.forEach(function (dot, dotIndex) {
          var isActive = dotIndex === index;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-selected', String(isActive));
        });
      }

      function next() { show(index + 1); }
      function prev() { show(index - 1); }

      function startAutoplay() {
        if (!autoplay || reduceMotion) return;
        stopAutoplay();
        timer = setInterval(next, speed);
      }
      function stopAutoplay() { if (timer) clearInterval(timer); }

      var nextBtn = hero.querySelector('[data-hero-next]');
      var prevBtn = hero.querySelector('[data-hero-prev]');
      if (nextBtn) nextBtn.addEventListener('click', function () { next(); startAutoplay(); });
      if (prevBtn) prevBtn.addEventListener('click', function () { prev(); startAutoplay(); });
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { show(i); startAutoplay(); });
      });

      hero.addEventListener('mouseenter', stopAutoplay);
      hero.addEventListener('mouseleave', startAutoplay);

      startAutoplay();
    });
  })();

  /* ============================================================
     Accordions (product info tabs, filter groups)
     ============================================================ */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-accordion-trigger]');
    if (!trigger) return;
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    var panel = trigger.nextElementSibling;
    if (panel) panel.classList.toggle('is-open', !expanded);
  });

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-filter-group-toggle]');
    if (!trigger) return;
    var expanded = trigger.getAttribute('aria-expanded') !== 'false';
    trigger.setAttribute('aria-expanded', String(!expanded));
    var body = trigger.nextElementSibling;
    if (body) body.style.display = expanded ? 'none' : '';
  });

  /* ============================================================
     Collection filters & sort — auto-submit on change
     ============================================================ */
  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-filter-input]')) {
      var form = e.target.closest('form');
      if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
    } else if (e.target.matches('[data-sort-select]')) {
      var sortForm = e.target.closest('form');
      if (sortForm) sortForm.requestSubmit ? sortForm.requestSubmit() : sortForm.submit();
    }
  });

  /* ============================================================
     Quantity steppers (cart items + product form)
     ============================================================ */
  document.addEventListener('click', function (e) {
    var incr = e.target.closest('[data-quantity-increase]');
    var decr = e.target.closest('[data-quantity-decrease]');
    if (!incr && !decr) return;
    var wrap = (incr || decr).closest('.quantity');
    var input = wrap && wrap.querySelector('[data-quantity-input]');
    if (!input) return;
    var value = parseInt(input.value, 10) || 1;
    var min = parseInt(input.min, 10) || 1;
    value = incr ? value + 1 : Math.max(min, value - 1);
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ============================================================
     Newsletter form: no JS needed (native Shopify customer form
     posts + redirects back with ?customer_posted=true), styling only.
     ============================================================ */

  /* ============================================================
     AJAX Cart
     ============================================================ */
  var Cart = (function () {
    function sectionsParam() {
      var sections = ['cart-drawer'];
      if (document.getElementById('MainCart')) sections.push('main-cart');
      return sections.join(',');
    }

    function updateBubble(count) {
      count = parseInt(count, 10) || 0;
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }

    function rebindDrawerScope(root) {
      // No-op hook: event listeners are all delegated on document,
      // so freshly-injected drawer markup works automatically.
      if (window.ClothifyProduct && root) window.ClothifyProduct.init(root);
    }

    function applySections(sections) {
      if (!sections) return;
      if (sections['cart-drawer']) {
        var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        var freshDrawer = parsed.getElementById('CartDrawer');
        var liveDrawer = document.getElementById('CartDrawer');
        if (freshDrawer && liveDrawer) {
          updateBubble(freshDrawer.dataset.cartItemCount);
          liveDrawer.innerHTML = freshDrawer.innerHTML;
          rebindDrawerScope(liveDrawer);
        }
      }
      if (sections['main-cart']) {
        var parsedMain = new DOMParser().parseFromString(sections['main-cart'], 'text/html');
        var freshMain = parsedMain.getElementById('MainCart');
        var liveMain = document.getElementById('MainCart');
        if (freshMain && liveMain) {
          liveMain.replaceWith(freshMain);
          rebindDrawerScope(freshMain);
        }
      }
    }

    function post(url, body, isJson) {
      var opts = { method: 'POST', headers: { Accept: 'application/json' } };
      if (isJson) {
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
      } else {
        opts.body = body;
      }
      return fetch(url, opts).then(function (res) { return res.json(); });
    }

    function addItem(formData) {
      formData.append('sections', sectionsParam());
      formData.append('sections_url', window.location.pathname);
      return post(theme.routes.cartAdd, formData, false).then(function (data) {
        if (data.status) {
          var err = new Error(data.description || 'Cart error');
          err.data = data;
          throw err;
        }
        applySections(data.sections);
        return data;
      });
    }

    function changeLine(line, quantity) {
      return post(theme.routes.cartChange, {
        line: line,
        quantity: quantity,
        sections: sectionsParam(),
        sections_url: window.location.pathname
      }, true).then(function (data) {
        applySections(data.sections);
        return data;
      });
    }

    return { addItem: addItem, changeLine: changeLine, updateBubble: updateBubble, applySections: applySections };
  })();
  window.ClothifyCart = Cart;

  // Product form submit (add to cart) — delegated, works for PDP and quick-add
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-product-form-el]');
    if (!form) return;
    e.preventDefault();

    var addBtn = form.querySelector('[data-add-to-cart]');
    var addBtnText = addBtn && addBtn.querySelector('[data-add-to-cart-text]');
    var originalText = addBtnText ? addBtnText.textContent : '';
    if (typeof form.clothifySyncSelectedVariant === 'function') {
      form.clothifySyncSelectedVariant();
    }
    if (addBtn) addBtn.disabled = true;
    if (addBtnText) addBtnText.textContent = theme.strings.adding;

    Cart.addItem(new FormData(form))
      .then(function () {
        if (addBtnText) addBtnText.textContent = theme.strings.added;
        if (theme.settings.cartType === 'page') {
          window.location.href = theme.routes.cartUrl;
          return;
        }
        openOverlay(document.getElementById('CartDrawer'));
        setTimeout(function () {
          if (addBtn) addBtn.disabled = false;
          if (addBtnText) addBtnText.textContent = originalText;
        }, 1800);
      })
      .catch(function (err) {
        if (addBtnText) addBtnText.textContent = (err.data && err.data.description) || theme.strings.soldOut;
        if (addBtn) addBtn.disabled = false;
        setTimeout(function () { if (addBtnText) addBtnText.textContent = originalText; }, 2200);
      });
  });

  // Cart line item quantity / remove — delegated
  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      Cart.changeLine(remove.dataset.line, 0);
    }
  });

  document.addEventListener('change', function (e) {
    if (e.target.matches('[data-cart-qty-input]')) {
      var qty = Math.max(0, parseInt(e.target.value, 10) || 0);
      Cart.changeLine(e.target.dataset.line, qty);
    }
  });

  document.addEventListener('click', function (e) {
    var incr = e.target.closest('[data-cart-qty-increase]');
    var decr = e.target.closest('[data-cart-qty-decrease]');
    if (!incr && !decr) return;
    var btn = incr || decr;
    var line = btn.dataset.line;
    var row = btn.closest('[data-cart-item]');
    var input = row && row.querySelector('[data-cart-qty-input]');
    var current = input ? parseInt(input.value, 10) || 0 : 0;
    var next = incr ? current + 1 : Math.max(0, current - 1);
    Cart.changeLine(line, next);
  });

  /* ============================================================
     Predictive search
     ============================================================ */
  (function initPredictiveSearch() {
    var input = document.querySelector('[data-search-input]');
    if (!input || !theme.settings.predictiveSearchEnabled) return;
    var resultsEl = document.getElementById('PredictiveSearchResults');
    var idleEl = document.querySelector('[data-search-idle-state]');

    var run = debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) {
        resultsEl.innerHTML = '';
        if (idleEl) idleEl.style.display = '';
        return;
      }
      if (idleEl) idleEl.style.display = 'none';
      var url = '/search/suggest.json?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last&section_id=search-drawer';
      fetch(url)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (!data.sections || !data.sections['search-drawer']) return;
          var parsed = new DOMParser().parseFromString(data.sections['search-drawer'], 'text/html');
          var fresh = parsed.getElementById('PredictiveSearchResults');
          if (fresh) resultsEl.innerHTML = fresh.innerHTML;
        })
        .catch(function () {});
    }, 300);

    input.addEventListener('input', run);

    document.querySelectorAll('[data-search-term]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        input.value = btn.dataset.searchTerm;
        input.focus();
        run();
      });
    });
  })();

  /* ============================================================
     Quick add modal
     ============================================================ */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-quick-add-open]');
    if (!opener) return;
    e.preventDefault();
    var modal = document.getElementById('QuickAddModal');
    var content = modal.querySelector('[data-quick-add-content]');
    content.innerHTML = '<div class="quick-add-loading" style="min-height:200px;display:flex;align-items:center;justify-content:center;color:var(--color-text-muted);">' + theme.strings.loading + '</div>';
    openOverlay(modal);
    fetch('/products/' + opener.dataset.productHandle + '?section_id=quick-add')
      .then(function (res) { return res.text(); })
      .then(function (html) {
        content.innerHTML = html;
        if (window.ClothifyProduct) window.ClothifyProduct.init(content);
      })
      .catch(function () {
        content.innerHTML = '<p>' + theme.strings.soldOut + '</p>';
      });
  });
})();
